import React, { useEffect, useState, useRef } from 'react';
import { triggerBlobDownload, extractFilename } from '../../utilities/download';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { get } from 'lodash';
import classnames from 'classnames';
import axios from 'axios';
import {
	SdfBox,
	SdfBusyIndicator,
	SdfButton,
	SdfDatePicker,
	SdfFocusPane,
	SdfIcon,
	SdfInput,
	SdfMultiSelect,
	SdfProgressBar,
	SdfPromoBox,
	SdfTooltip
} from '@synerg/react-components';
import { updateActiveChangeRequest, useApplicationState, showErrorModal } from '../../utilities/applicationState';
import PreAuthEmail from '../partnerProjectList/partnerProjectDetailsSlideIn/preAuthEmail';
import { getFormattedCutOffDate, getFormattedStringDate, adjustTimezone } from '../common';
import SubscriptionsTable from './subscriptionsTable';
import './subscriptionsDetails.scss';

const TYPES = { PROD: 'PROD', DEV: 'DEV' };

export default function SubscriptionsDetails({ retrieveOrgName, onSubscriptionDetailsChange, projectId,
	prodProjectId, isFromProjects = false, projectType = '', consentURLPendingEmail = '', onModifyAppScope }) {

	const [store, dispatch] = useApplicationState();
	const [selectedProjectType, setSelectedProjectType] = useState(projectType);
	const [loadingComponent, setLoadingComponent] = useState(true);
	const [filterClientOrOrg, setClientOrOrg] = useState('');
	const [filterSubStatus, setSubStatus] = useState([]);
	const [activeCR, setActiveCR] = useState(false);
	const [cutOffDate, setCutOffDate] = useState();
	const [cutOffDateApplied, setCutOffDateApplied] = useState();
	const [cancelableRequest, setCancelableRequest] = useState(false);
	const [reportDownload, setReportDownload] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [isReportDownloading, setIsReportDownloading] = useState(false);
	const [subsReportPercentComplete, setSubsReportPercentComplete] = useState(0);
	const [subsReportMinutesToComplete, setSubsReportMinutesToComplete] = useState(0);
	const [isSubsReportCached, setIsSubsReportCached] = useState(false);
	const [reportFile, setReportFile] = useState({});
	const [showEmailIcon, setShowEmailIcon] = useState(false);
	const searchDebounce = useRef(null);
	const cancelRef = useRef(null);
	const [consentURL, setConsentURL] = useState('');
	const [subscriptionStatusOptions] = useState([
		{ key: 'Active', value: 'Active', label: 'Active' },
		{ key: 'Suspended', value: 'Suspended', label: 'Suspended' },
		{ key: 'Canceled	', value: 'Canceled', label: 'Canceled' }
	]);
	const current = store.projects.current || {};
	const carDetails = current.projectCarDetails || {};
	const hasProdData = isFromProjects ? projectType === 'PROD' : get(current, 'devInfo.link', '') !== '';
	const { loadingProjectInfo = true, isLoadingCarDetails = true, isLoadingSubscriptions = {} } = store.loadings;

	const onSearch = search => {
		clearTimeout(searchDebounce.current);
		searchDebounce.current = setTimeout(() => {
			const newSearch = (search || '').trim();
			setClientOrOrg(newSearch);
			onSubscriptionDetailsChange({
				search: newSearch,
				subscriptions: filterSubStatus,
				projectType: selectedProjectType
			});
		}, 800);
	};

	const onSubscriptionFilter = status => {
		setSubStatus(status);
		onSubscriptionDetailsChange({
			search: filterClientOrOrg,
			subscriptions: status,
			projectType: selectedProjectType
		});
	};

	const getCancelableRequestToken = () => {
		if (cancelableRequest) {
			cancelableRequest.cancel('search request canceled');
		}
		const newRequest = axios.CancelToken.source();
		setCancelableRequest(newRequest);
		return newRequest;
	};

	const closeDownloadModal = () => {
		cancelRef.current = null;
		setOpenModal(false);
		setReportDownload(false);
		setSubsReportPercentComplete(0);
	};

	const timer = ms => new Promise(res => setTimeout(res, ms));


	const generateReport = async () => {
		setIsReportDownloading(true);
		const pId = selectedProjectType === 'PROD' ? prodProjectId : projectId
		axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project/${pId}/subscriptions-download/generate`, null, {
			cancelToken: getCancelableRequestToken().token,
			// remove after initial testing
			params: { projectId: selectedProjectType === 'PROD' ? prodProjectId : projectId }
		}).then(async (response) => {
      if (response.data.downloadUrl) {
        setIsSubsReportCached(true);
        setSubsReportPercentComplete(100);
				return getFile(response.data.downloadUrl);
      }
      else if (response.data.trackingUrl) {
        setIsSubsReportCached(false);
				await getStatus(response.data.trackingUrl);
			} else {
        throw new Error('Error geneerating report.');
      }
		}).catch(async (e) => {
				const errorMessage = get(e, 'response.data.message', 'Error');
				closeDownloadModal();
				dispatch(showErrorModal(errorMessage));
		})
	};


	const getStatus = async (trackingUrl) => {
		try {
			let i = 0
			while (i++ < 1000) {
				const { data: { percentComplete = 0, minutesToComplete = 0, downloadUrl = null, } } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}${trackingUrl}`, {
					cancelToken: getCancelableRequestToken().token,
					timeout: 45000
				})

				if (downloadUrl) {
					setSubsReportPercentComplete(100);
					return getFile(downloadUrl);
				} else {
					setSubsReportPercentComplete(percentComplete);
					setSubsReportMinutesToComplete(minutesToComplete);
				}

				await timer( 1000 + 200 * minutesToComplete ** 0.75  ); // poll slightly less frequently for larger files
			}
		} catch (e) {
			const errorMessage = e.response.data.message || 'There was an error generating the file.';
			closeDownloadModal();
			return dispatch(showErrorModal(errorMessage));
		}
	};


	const getFile = async (downloadUrl) => {
		await axios.get(`${import.meta.env.VITE_ROOT_PATH}${downloadUrl}`, {
			cancelToken: getCancelableRequestToken().token,
			responseType: 'blob'
		}).then((response) => {
			setReportFile(response);
			setIsReportDownloading(false);
		}).catch((e) => {
			const errorMessage = get(e, 'response.data.message', 'Error');
			closeDownloadModal();
			dispatch(showErrorModal(errorMessage));
		})
	};


	const downloadFile = async () => {
		triggerBlobDownload(new Blob([reportFile.data]), extractFilename(reportFile.headers["content-disposition"]));
		closeDownloadModal();
	};


	// initial loading state
	useEffect(() => {
		if (loadingComponent && !isLoadingCarDetails) {
			setSelectedProjectType(hasProdData ? TYPES.PROD : TYPES.DEV);
			setLoadingComponent(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingCarDetails]);

	// effect for initial project type
	useEffect(() => {
		if (!loadingComponent && hasProdData && selectedProjectType === '') {
			setSelectedProjectType(hasProdData ? TYPES.PROD : TYPES.DEV);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [carDetails]);

	// switch project in table loading state effect
	useEffect(() => {
		if (loadingProjectInfo) {
			setLoadingComponent(true);
		} else {
			setLoadingComponent(false);
		}
	}, [loadingProjectInfo]);

	useEffect(() => {
		if (reportDownload) {
			setOpenModal(true);
			generateReport();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reportDownload]);

	useEffect(() => {
		if (isFromProjects) {
			setConsentURL(consentURLPendingEmail);
		} else {
			setConsentURL(selectedProjectType === TYPES.PROD ? get(store.projects.current, 'prodInfo.consentURLPendingEmail') : get(store.projects.current, 'devInfo.consentURLPendingEmail'));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFromProjects, consentURLPendingEmail, selectedProjectType]);

	useEffect(() => {
		async function getActiveCR(selectedProjectId) {
			try {
				setActiveCR(false);
				const { data: { success = false, changeRequest } } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/${selectedProjectId}/change-request/active`);
				if (success) {
					setActiveCR(changeRequest);
					setCutOffDate(getFormattedStringDate(adjustTimezone(changeRequest.cutOffDate)));

					if (get(changeRequest, 'preAuthRequired', false) &&
						(get(changeRequest, 'applicationType', '') === 'B2B') &&
						(get(changeRequest, 'environment', '') === 'PROD')) {
						setShowEmailIcon(true);
					}
					dispatch(updateActiveChangeRequest(changeRequest));
				}
			} catch (e) {
				setActiveCR(false);
			}
		}

		if (selectedProjectType) {
			let selectedProjectId = projectId;
			if (selectedProjectType === TYPES.PROD) {
				selectedProjectId = prodProjectId;
			}
			setCutOffDateApplied();
			getActiveCR(selectedProjectId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedProjectType]);

	const updateCutOffDate = async () => {
		try {
			setCutOffDateApplied();
			const { data: { success = false } } = await axios.put(`${import.meta.env.VITE_ROOT_PATH}/api/project/change-request/${activeCR._id}`, {
				cutOffDate: getFormattedCutOffDate(cutOffDate)
			});
			if (success) {
				setCutOffDateApplied(true);
			} else {
				setCutOffDateApplied(false);
			}
		} catch (e) {
			setCutOffDateApplied(false);
		}
	};

	const loadingSubscriptions = isLoadingCarDetails || loadingProjectInfo || loadingComponent;
	const prodSubsCount = get(carDetails, 'prod.subscriptionsTotal', 0);
	const devSubsCount = get(carDetails, 'subscriptionsTotal', 0);
	const prodSubsCountIndicator = loadingComponent ? '...' : prodSubsCount;
	const devSubsCountIndicator = loadingComponent ? '...' : devSubsCount;
	const isProd = selectedProjectType === TYPES.PROD || projectType === TYPES.PROD;
	const subscriptionTotal = isProd ? prodSubsCountIndicator : devSubsCountIndicator;
	const isReportDisabled = subscriptionTotal < 1 ? true : false;
	const subscriptions = isProd
		? get(carDetails, 'prod.subscriptions', [])
		: get(carDetails, 'subscriptions', []);

	const preAuthRequiredCount = get(subscriptions, 'preAuth.preAuthRequiredCount', []);

	const preAuthSubs = get(subscriptions, 'requestsForAuth.subscriptions', []);

	let hasActiveSubscriptionThatNeedsPreAuth = false;
	preAuthSubs.forEach((preAuthSubs => {
		if (preAuthSubs.subscriptionStatusCode === 'Active') {
			hasActiveSubscriptionThatNeedsPreAuth = true;
		}
	}));

	const subTitleProjectView = isFromProjects && projectType === 'PROD' ? 'Live' : 'Test';
	const subTitleAdminView = !isFromProjects && selectedProjectType === 'PROD' ? 'PROD' : 'DEV';
	const hasSubscriptions = selectedProjectType === 'PROD' ? prodSubsCount > 0 : devSubsCount > 0;
	const subsTitle = isFromProjects ? subTitleProjectView : subTitleAdminView;
	return (
		<div>
			{loadingSubscriptions &&
				<SdfBusyIndicator
					style={{ color: "#1a8099" }}
					size="xl"
					fullScreen={true}
				/>
			}
			<div className="flex mt-10 gap-2 justify-between items-center">
				<div className="flex gap-3 items-center flex-2">
					<div className="text-heading-07 font-black">{!loadingComponent && `${subsTitle} Subscriptions`}</div>
					<SdfButton
						className="pt-4"
						emphasis="primary"
						icon="action-download"
						variant="text"
						onClick={() => setReportDownload(true)}
						{...(isReportDisabled && { disabled: true })}
					>
						<a href="#!" id="download-subscription-table-csv-anchor" style={{ display: 'none' }}>DOWNLOAD REPORT</a>
						Download report
					</SdfButton>
				</div>
				<div className="flex justify-end gap-2 flex-1">
					<SdfInput
						className="w-1/2"
						label="Search"
						placeholder="Search by client name or organization ID"
						value={filterClientOrOrg}
						onSdfInput={($event) => {
							onSearch($event.detail)
						}}
					>
						<SdfIcon slot="after" icon="action-search" />
					</SdfInput>
					<SdfMultiSelect
						placeholder="Select..."
						label="Subscription Status"
						optionTerm="Selected"
						onSdfChange={({ detail }) => { onSubscriptionFilter(detail) }}
						items={subscriptionStatusOptions}
						isDisabled={!hasSubscriptions && !filterSubStatus.length > 0}
						value={filterSubStatus}
						inputWidth="md"
					/>
				</div>
			</div>

			{!isFromProjects && <div className="download-subscription-report-button">
				<SdfButton buttonStyle="primary" onClick={onModifyAppScope}>
					MODIFY APP SCOPE
				</SdfButton>
			</div>}

			<div className={classnames('flex justify-between pt-8', isProd && 'pt-12')}>
				{consentURL &&
					<SdfPromoBox
						className="w-full"
						header="Need to resend a client authorization link?"
						illustrationName="email"
						variant="bordered"
						spacing="loose"
						layout="horizontal"
						message={consentURL}
					>
						<div className="text-center" slot="cta">
							<CopyToClipboard text={consentURL}>
								<SdfTooltip positionContext="ELEMENT" trigger="click" state="info" attachmentPoint="top">
									<SdfButton emphasis="secondary" className="ml-5" slot="tooltip-target"> Copy link </SdfButton>
									Copied
								</SdfTooltip>
							</CopyToClipboard>
						</div>
					</SdfPromoBox>
				}
				{activeCR && isProd && <SdfBox variant="bordered" className="ml-6 p-4 min-w-1/3">
					<div className={`cut-off-date ${isFromProjects ? 'partner-view' : ''}`}>
						<div className="w-full inline-flex" style={{ color: "#983f00" }}>
							<SdfIcon icon={"status-flagged"} className="pr-2" />
							<div className="text-body-02">
								{`${preAuthRequiredCount} customer${preAuthRequiredCount > 1 ? 's' : ''} require${preAuthRequiredCount > 1 ? '' : 's'} pre-authorization`}
							</div>
						</div>
						<div className="mt-3">
							<SdfDatePicker
								label="Cutoff date"
								rules={[{
									type: "DISABLED",
									startDateInclusive: new Date(0, 0, 0),
									endDateInclusive: new Date().setDate(new Date().getDate() - 1)
								}]}
								value={cutOffDate}
								onSdfChange={({ detail }) => {
									setCutOffDate(detail);
								}}
								placeholder="MM/DD/YYYY"
								disabled={isFromProjects}
							/> {!isFromProjects &&
								<SdfButton buttonStyle='link' onClick={updateCutOffDate}>Update</SdfButton>
							}
						</div>
						{cutOffDateApplied === false && <div className="message-container">
							<SdfIcon icon="alert-error" />
							<div className="message">We are sorry about the inconvenience. Please try again.</div>
						</div>}
						{cutOffDateApplied === true && <div className="message-container">
							<SdfIcon icon="action-confirm" />
							<div className="message">Cutoff date has been updated successfully.</div>
						</div>}
					</div>
				</SdfBox>}
			</div>
			<div>
				<div className={classnames('sections-container pt-8', isProd && 'pt-12')}>
					<div className={classnames(get(subscriptions, 'requestsForAuth.subscriptionList', []).length === 0 && 'pb-12')}>
						<div className="text-heading-06 font-black">Requests for authorization ({get(subscriptions, 'requestsForAuth.subscriptionList', []).length})</div>
						{showEmailIcon && selectedProjectType === TYPES.PROD &&
							(get(subscriptions, 'requestsForAuth.subscriptionList', []).length > 0) && hasActiveSubscriptionThatNeedsPreAuth &&
							<PreAuthEmail activeCR={activeCR} />
						}
						<div className="pt-2 pb-4">Client authorization requests are awaiting action from customer(s) listed below:</div>
						<SubscriptionsTable {...{
							retrieveOrgName,
							projectId,
							subscriptionList: get(subscriptions, 'requestsForAuth.subscriptionList', []),
							isLoadingSubscriptions: isLoadingSubscriptions['requestsForAuth'],
							type: "requestsForAuth",
							cutOffDate,
							selectedProjectType,
							activeCR,
							filterClientOrOrg,
							filterSubStatus
						}} />
					</div>
					<div className={classnames(get(subscriptions, 'deniedAuth.subscriptionList', []).length === 0 && 'pb-12')}>
						<div className="text-heading-06 font-black">Denied authorization ({get(subscriptions, 'deniedAuth.subscriptionList', []).length})</div>
						<div className="pt-2 pb-4">Client authorization requests were denied by the customer(s) listed below.</div>
						<SubscriptionsTable {...{
							retrieveOrgName,
							projectId,
							subscriptionList: get(subscriptions, 'deniedAuth.subscriptionList', []),
							isLoadingSubscriptions: isLoadingSubscriptions['deniedAuth'],
							type: "deniedAuth",
							selectedProjectType,
							filterClientOrOrg,
							filterSubStatus
						}} />
					</div>
					<div className={classnames(get(subscriptions, 'allowedAuth.subscriptionList', []).length === 0 && 'pb-12')}>
						<div className="text-heading-06 font-black">Allowed authorization ({get(subscriptions, 'allowedAuth.subscriptionList', []).length})</div>
						<div className="pt-2 pb-4">Client authorization requests were allowed by the customer(s) listed below.</div>
						<SubscriptionsTable {...{
							retrieveOrgName,
							projectId,
							isLoadingSubscriptions: isLoadingSubscriptions['allowedAuth'],
							subscriptionList: get(subscriptions, 'allowedAuth.subscriptionList', []),
							type: "allowedAuth",
							selectedProjectType,
							filterClientOrOrg,
							filterSubStatus
						}} />
					</div>
				</div>
			</div>

			{openModal &&
				<SdfFocusPane
					paneType="floating"
					visible={true}
					acceptLabel="Download"
					dismissLabel="Cancel"
					onSdfAccept={() => {
						downloadFile();
					}}
					onSdfDismiss={() => {
						closeDownloadModal();
					}}
					closeable={true}
					size="sm"
					backdrop={true}
					keyboard={false}
					animation={true}
					show={true}
					busy={isReportDownloading}
				>
					<div slot="title">Download Report</div>
					<div className="px-10">
						<h3>{isSubsReportCached ?
              "Cached report found."
              :
              `${subsReportPercentComplete}% completed.`
            }</h3>
						<div>
							{subsReportPercentComplete >= 0 && subsReportPercentComplete < 100 && <SdfProgressBar
								size="sm"
								value={subsReportPercentComplete}
								status="in-progress"
							/>}
						</div>
						<span className="report-generating-text">
							{subsReportPercentComplete < 100 ?
                subsReportPercentComplete >= 2 ? `Time to completion: ${subsReportMinutesToComplete > 1 ? `${subsReportMinutesToComplete} minutes` : 'less than a minute'}.` : ''
                :
                "Press the Download button to save the file."}
						</span>
					</div>
				</SdfFocusPane>
			}
		</div>
	);
}
